import { RefObject, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useResizeObserver } from '@cutwise/react-utils';
import { getScrollableAncestors } from '@dnd-kit/core';

export interface VirtualScrollArea {
  scrollElement: Nullable<HTMLElement>;
  scrollMargin: number;
}

const DETACHED_SCROLL_AREA: VirtualScrollArea = { scrollElement: null, scrollMargin: 0 };

export function useVirtualScrollArea<T extends HTMLElement = HTMLDivElement>(): [
  RefObject<T | null>,
  VirtualScrollArea,
] {
  const listRef = useRef<T>(null);
  const [scrollArea, setScrollArea] = useState<VirtualScrollArea>(DETACHED_SCROLL_AREA);

  const updateScrollArea = useCallback(() => {
    const nextScrollArea = readScrollArea(listRef.current);

    setScrollArea((currentScrollArea) => {
      if (isSameScrollArea(currentScrollArea, nextScrollArea)) {
        return currentScrollArea;
      }

      return nextScrollArea;
    });
  }, []);

  useResizeObserver<T>({ ref: listRef, onResize: updateScrollArea });

  useLayoutEffect(updateScrollArea, [updateScrollArea]);

  return [listRef, scrollArea];
}

function readScrollArea(listElement: Nullable<HTMLElement>): VirtualScrollArea {
  const scrollElement = findScrollableAncestor(listElement);

  if (!listElement || !scrollElement) {
    return DETACHED_SCROLL_AREA;
  }

  return { scrollElement, scrollMargin: getScrollMargin(listElement, scrollElement) };
}

function findScrollableAncestor(listElement: Nullable<HTMLElement>): Nullable<HTMLElement> {
  if (!listElement) {
    return null;
  }

  const [scrollableAncestor] = getScrollableAncestors(listElement, 1);

  if (scrollableAncestor instanceof HTMLElement) {
    return scrollableAncestor;
  } else {
    return listElement.ownerDocument.documentElement;
  }
}

function getScrollMargin(listElement: HTMLElement, scrollElement: HTMLElement): number {
  const listTop = listElement.getBoundingClientRect().top;
  const scrollTop = scrollElement.getBoundingClientRect().top;

  return Math.round(listTop - scrollTop + scrollElement.scrollTop);
}

function isSameScrollArea(left: VirtualScrollArea, right: VirtualScrollArea): boolean {
  return left.scrollElement === right.scrollElement && left.scrollMargin === right.scrollMargin;
}
